<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useProjectionsStore } from '../stores/projections'
import { useSplitViewStore } from '../stores/splitViewStore'
import GlobalParametersSidebar from './GlobalParametersSidebar.vue'
import axios from '../axios'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  fixtureId: {
    type: [Number, String],
    required: false
  }
})

const emit = defineEmits(['close', 'globalParametersUpdated'])
const projectionsStore = useProjectionsStore()
const splitViewStore = useSplitViewStore()

// Split view mode
const isSplitMode = computed(() => splitViewStore.isSplitMode)

// Get data from projections store
const projectionsData = computed(() => projectionsStore.projectionsData)

// Available seasons and leagues
const seasons = [
  '2025-2026', '2024-2025', '2023-2024', '2022-2023', '2021-2022', '2020-2021', '2019-2020','2018-2019', '2017-2018'
]

// Global filter state
const selectedSeason = ref([])
const selectedLeagues = ref([])
const selectedAdditionalFilter = ref('allGames')
const selectedTeam = ref('home') // 'home', 'away', or 'both'

// Application mode: 'merge' or 'overwrite'
const applicationMode = ref('overwrite') // 'merge' or 'overwrite'

// Notification state
const showNotification = ref(false)
const notificationData = ref({
  type: 'success', // 'success' or 'error'
  title: '',
  message: '',
  details: {}
})

// Games data for preview
const filteredGames = ref([])
const loadingGames = ref(false)

// Get available leagues
function sortByAZ(arr) {
  return [...arr].sort((a, b) => a.name.localeCompare(b.name));
}

const availableLeagues = computed(() => {
  const LEAGUES = [
    { id: 'friendly-international', name: 'Friendly International' },
    { id: 'adriatic-aba-liga-2', name: 'Adriatic ABA Liga 2' },
    { id: 'adriatic-liga-aba', name: 'Adriatic Liga ABA' },
    { id: 'adriatic-liga-aba-playoffs', name: 'Adriatic Liga ABA Playoffs' },
    { id: 'argentina-la-liga-torneo-super-20', name: 'Argentina La Liga Torneo Super 20' },
    { id: 'argentina-liga-a', name: 'Argentina Liga A' },
    { id: 'australia-nbl', name: 'Australia NBL' },
    { id: 'austria-superliga', name: 'Austria Superliga' },
    { id: 'basketball-champions-league', name: 'Basketball Champions League' },
    { id: 'basketball-champions-league-americas', name: 'Basketball Champions League Americas' },
    { id: 'bnxt-league', name: 'BNXT League' },
    { id: 'bosnia-division-i', name: 'Bosnia Division I' },
    { id: 'bulgaria-nbl', name: 'Bulgaria NBL' },
    { id: 'china-cba', name: 'China CBA' },
    { id: 'czech-republic-mattoni-nbl', name: 'Czech Republic Mattoni NBL' },
    { id: 'denmark-ligaen', name: 'Denmark Ligaen' },
    { id: 'eurocup', name: 'EuroCup' },
    { id: 'euroleague', name: 'EuroLeague' },
    { id: 'fiba-europe-cup', name: 'FIBA Europe Cup' },
    { id: 'finland-korisliiga', name: 'Finland Korisliiga' },
    { id: 'france-betclic-elite', name: 'France Betclic Elite' },
    { id: 'france-betclic-elite-playoffs', name: 'France Betclic Elite Playoffs' },
    { id: 'france-elite-2', name: 'France Elite 2' },
    { id: 'georgia-superleague', name: 'Georgia Superleague' },
    { id: 'germany-easycredit-bbl', name: 'Germany EasyCredit BBL' },
    { id: 'germany-pro-a', name: 'Germany Pro A' },
    { id: 'greece-heba-a1', name: 'Greece HEBA A1' },
    { id: 'greece-heba-a2', name: 'Greece HEBA A2' },
    { id: 'hungary-a-league', name: 'Hungary A League' },
    { id: 'iceland-express-league', name: 'Iceland Express League' },
    { id: 'israel-winner-league', name: 'Israel Winner League' },
    { id: 'italy-lba-serie-a', name: 'Italy LBA Serie A' },
    { id: 'italy-serie-a2', name: 'Italy Serie A2' },
    { id: 'japan-b1-league', name: 'Japan B1 League' },
    { id: 'japan-b2-league', name: 'Japan B2 League' },
    { id: 'latvian-estonian-basketball-league', name: 'Latvian-Estonian Basketball League' },
    { id: 'lithuania-betsafe-lkl', name: 'Lithuania Betsafe LKL' },
    { id: 'lithuania-betsafe-lkl-playoffs', name: 'Lithuania Betsafe LKL Playoffs' },
    { id: 'lithuania-nkl', name: 'Lithuania NKL' },
    { id: 'mexico-liga-sisnova-lnbp', name: 'Mexico Liga Sisnova LNBP' },
    { id: 'nba', name: 'NBA' },
    { id: 'wnba', name: 'WNBA' },
    { id: 'ncaa', name: 'NCAA' },
    { id: 'new-zealand-nbl', name: 'New Zealand NBL' },
    { id: 'norway-blno', name: 'Norway BLNO' },
    { id: 'poland-plk', name: 'Poland PLK' },
    { id: 'portugal-liga-profissional', name: 'Portugal Liga Profissional' },
    { id: 'puerto-rico-bsn', name: 'Puerto Rico BSN' },
    { id: 'romania-division-a', name: 'Romania Division A' },
    { id: 'serbia-kls', name: 'Serbia KLS' },
    { id: 'slovakia-extraliga', name: 'Slovakia Extraliga' },
    { id: 'slovenia-liga-nova-kbm', name: 'Slovenia Liga Nova KBM' },
    { id: 'spain-leb-gold', name: 'Spain LEB Gold' },
    { id: 'spain-liga-endesa', name: 'Spain Liga Endesa' },
    { id: 'super-league-basketball', name: 'Super League Basketball' },
    { id: 'sweden-basketligan', name: 'Sweden Basketligan' },
    { id: 'switzerland-sbl', name: 'Switzerland SBL' },
    { id: 'turkey-bsl', name: 'Turkey BSL' },
    { id: 'turkey-tbl', name: 'Turkey TBL' },
    { id: 'vtb-united-league', name: 'VTB United League' },
    { id: 'world-cup', name: 'World Cup' },
    { id: 'eurobasket', name: 'EuroBasket' },
    { id: 'eurobasket-2025', name: 'EuroBasket 2025' },
    { id: 'world-cup-european-qualifiers', name: 'World Cup European Qualifiers' },
    { id: 'eurobasket-qualifying-rounds', name: 'EuroBasket Qualifying Rounds' },
    { id: 'olympic-games', name: 'Olympic Games' },
    { id: 'olympic-games-qualifying-tournament', name: 'Olympic Games Qualifying Tournament' },
    { id: 'preparation', name: 'Preparation' },
    { id: 'cyprus-division-a', name: 'Cyprus Division A' },
    { id: 'basketball-champions-league-qualifying-rounds', name: 'Basketball Champions League Qualifying Rounds' },
    { id: 'fiba-europe-cup-qualifying-rounds', name: 'FIBA Europe Cup Qualifying Rounds' },
    { id: 'croatia-liga', name: 'Croatia Liga' },
    { id: 'enbl', name: 'ENBL' },
    { id: 'czech-republic-mattoni-nbl', name: 'Czech Republic Mattoni NBL' },
  { id: 'brazil-nbb', name: 'Brazil NBB' },
  { id: 'brazilian-nbb', name: 'Brazilian NBB' },
  { id: 'argentina-liga-a', name: 'Argentina Liga A' },
  { id: 'king-mindaugas-cup-play-offs', name: 'King Mindaugas Cup Play Off' },
  { id: 'latvian-cup', name: 'Latvian Cup' },
  { id: 'enbl', name: 'ENBL' },
  { id: 'turkish-cup', name: 'Turkish Cup' },
  { id: 'italian-cup', name: 'Italian Cup' },
  { id: 'spanish-cup', name: 'Spanish Cup' },
  { id: 'world-cup-european-pre-qualifiers', name: 'World Cup European Pre Qualifiers' },
  ]
  return sortByAZ(LEAGUES);
})

// Watch for changes and apply globally
watch([selectedLeagues, selectedSeason, selectedAdditionalFilter], async ([leagues, seasons, additionalFilter]) => {
  console.log('Global parameters changed:', { leagues, seasons, additionalFilter })
  
  // Apply global filters to all players in the projections store
  if (projectionsStore.projectionsData?.fixture_projections) {
    projectionsStore.projectionsData.fixture_projections.forEach(player => {
      if (player) {
        player.selected_leagues = [...leagues]
        player.seasons = [...seasons]
        // Note: additionalFilter is handled per-player, but we can set a global default
      }
    })
    
    // Emit global parameters update event
    emit('global-parameters-updated', {
      seasons: [...seasons],
      leagues: [...leagues],
      additionalFilter: additionalFilter
    })
  }
  
  // Fetch games preview when filters change
  await fetchGamesPreview()
}, { deep: true })

// Fetch games preview based on current filters
async function fetchGamesPreview() {
  if (selectedSeason.value.length === 0 && selectedLeagues.value.length === 0) {
    filteredGames.value = []
    return
  }
  
  loadingGames.value = true
  try {
    // Get a sample player ID to fetch games (we'll use the first available player)
    const samplePlayer = projectionsStore.projectionsData?.fixture_projections?.[0]
    if (!samplePlayer) {
      filteredGames.value = []
      return
    }
    
    const requestBody = {
      player_id: samplePlayer.player_id,
      seasons: selectedSeason.value,
      leagues: selectedLeagues.value,
      additional_filter: selectedAdditionalFilter.value,
      fixtureId: projectionsStore.projectionsData.id
    }
    
    const response = await axios.post('/api/players/games', requestBody)
    let data = response.data
    
    // Handle different possible response structures
    if (Array.isArray(data)) {
      data = data
    } else if (data && Array.isArray(data.games)) {
      data = data.games
    } else if (data && Array.isArray(data.data)) {
      data = data.data
    } else {
      const gameArray = Object.values(data).filter(item => typeof item === 'object' && item.id)
      data = gameArray
    }
    
    // Apply additional filtering
    data = applyAdditionalFilter(data, selectedAdditionalFilter.value)
    
    // Show all matching games
    filteredGames.value = data
    
  } catch (error) {
    console.error('Error fetching games preview:', error)
    filteredGames.value = []
  } finally {
    loadingGames.value = false
  }
}

// Function to apply additional filtering based on the selected option
function applyAdditionalFilter(games, filterType) {
  if (!games || games.length === 0) return games
  
  switch (filterType) {
    case 'lastFiveGames':
      const sortedGames = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sortedGames.slice(0, 5)
      
    case 'withoutLast5Games':
      const sortedGamesForRest = [...games].sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
      return sortedGamesForRest.slice(5)
      
    case 'allGames':
    default:
      return games
  }
}

onMounted(async () => {
  try {
    // Get intersection filters from backend API
    await fetchDefaultFilters()
    
  } catch (error) {
    console.error('Error initializing global parameters:', error)
  }
})

// Watch for modal opening to fetch defaults
watch(() => props.show, async (isOpen) => {
  if (isOpen) {
    console.log('Modal opened, fetching default filters...')
    try {
      await fetchDefaultFilters()
    } catch (error) {
      console.error('Error fetching default filters:', error)
    }
  }
}, { immediate: true })

// Fetch default filters from backend
async function fetchDefaultFilters() {
  try {
    const fixtureId = props.fixtureId || projectionsStore.projectionsData?.id
    console.log('Fetching default filters for fixture ID:', fixtureId)
    console.log('Selected team:', selectedTeam.value)
    
    if (!fixtureId) {
      console.warn('No fixture ID available for fetching default filters')
      return
    }
    
    console.log('Making API request to:', `/api/projections/global-filter/defaults/${fixtureId}`)
    const response = await axios.get(`/api/projections/global-filter/defaults/${fixtureId}`, {
      params: {
        team_type: selectedTeam.value
      }
    })
    
    console.log('API response:', response.data)
    
    const data = response.data
    
    selectedSeason.value = data.seasons || []
    selectedLeagues.value = data.leagues || []
    selectedAdditionalFilter.value = data.additional_filter || 'allGames'
    
    console.log('Global parameters initialized from backend:', {
      seasons: selectedSeason.value,
      leagues: selectedLeagues.value,
      additionalFilter: selectedAdditionalFilter.value,
      playerCount: data.player_count,
      teamType: data.team_type
    })
    
  } catch (error) {
    console.error('Error fetching default filters from backend:', error)
    // Fallback to store values if backend fails
    selectedSeason.value = [...projectionsStore.getFilterSeasons]
    selectedLeagues.value = [...projectionsStore.getFilterLeagues]
    selectedAdditionalFilter.value = 'allGames'
  }
}

// Note: Frontend intersection logic removed - now using backend API

function closeModal() {
  emit('close')
}

// Toggle split mode
function toggleSplitMode() {
  if (isSplitMode.value) {
    splitViewStore.disableSplitMode()
  } else {
    splitViewStore.enableSplitMode()
  }
}

// Watch for split mode changes
watch(isSplitMode, (newValue) => {
  if (!newValue) {
    // Optional: close modal when exiting split mode
    // emit('close')
  }
})

// Watch for team selection changes and refetch defaults
watch(selectedTeam, async (newTeam) => {
  console.log('Team selection changed to:', newTeam)
  await fetchDefaultFilters()
}, { immediate: false })

// Watch for changes and update preview only (no API calls)
watch([selectedLeagues, selectedSeason, selectedAdditionalFilter], async ([leagues, seasons, additionalFilter]) => {
  try {
    // Update store with new global filters (only if these methods exist)
    if (typeof projectionsStore.setFilterSeasons === 'function') {
      projectionsStore.setFilterSeasons(seasons)
    }
    if (typeof projectionsStore.setFilterLeagues === 'function') {
      projectionsStore.setFilterLeagues(leagues)
    }
    if (typeof projectionsStore.setAdditionalFilter === 'function') {
      projectionsStore.setAdditionalFilter(additionalFilter)
    }
    
    // Emit event to parent component
    emit('globalParametersUpdated', {
      seasons: seasons,
      leagues: leagues,
      additionalFilter: additionalFilter
    })
    
    // Fetch games preview when filters change (preview only, no API calls)
    await fetchGamesPreview()
  } catch (error) {
    console.error('Error in watch callback:', error)
  }
}, { deep: true })

// Apply global parameters to specific team(s)
async function applyGlobalParameters() {
  try {
    const globalParams = {
      fixture_id: props.fixtureId || projectionsStore.projectionsData.id || 3,
      team_type: selectedTeam.value, // 'home', 'away', or 'both'
      seasons: selectedSeason.value,
      leagues: selectedLeagues.value,
      additional_filter: selectedAdditionalFilter.value,
      application_mode: applicationMode.value // 'merge' or 'overwrite'
    }
    
    console.log('Applying global parameters:', globalParams)
    
    // Send to API to update players for specific team(s)
    const response = await axios.post('/api/projections/global-filter', globalParams)
    
    if (response.data) {
      console.log('Global parameters applied successfully:', response.data)
      console.log(`Applied to ${response.data.updated_count} players in ${response.data.team_type} team`)
      
      // Show success notification
      showSuccessNotification(response.data)
      
      // Emit success event to parent
      emit('globalParametersUpdated', {
        success: true,
        message: response.data.message,
        updatedCount: response.data.updated_count,
        teamType: response.data.team_type
      })
    }
    
  } catch (error) {
    console.error('Error applying global parameters:', error)
    
    // Show error notification
    showErrorNotification(error)
    
    // Emit error event to parent
    emit('globalParametersUpdated', {
      success: false,
      error: error.message
    })
  }
}

// Show success notification
function showSuccessNotification(data) {
  notificationData.value = {
    type: 'success',
    title: 'Global Filters Applied Successfully!',
    message: data.message,
    details: {
      updatedCount: data.updated_count,
      teamType: data.team_type,
      applicationMode: applicationMode.value,
      appliedFilters: data.applied_filters
    }
  }
  showNotification.value = true
}

// Show error notification
function showErrorNotification(error) {
  notificationData.value = {
    type: 'error',
    title: 'Error Applying Global Filters',
    message: error.response?.data?.message || error.message || 'An unexpected error occurred',
    details: {
      error: error.message,
      status: error.response?.status
    }
  }
  showNotification.value = true
}

// Close notification
function closeNotification() {
  showNotification.value = false
}
</script>

<template>
  <!-- Normal Modal Mode -->
  <div v-if="show && !isSplitMode" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-2">
      <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="emit('close')">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <div class="relative w-full h-[95vh] bg-white rounded-lg shadow-xl flex overflow-hidden">
        <!-- Modal Content -->
        <div class="w-full h-full flex">
          <!-- Sidebar -->
          <GlobalParametersSidebar
            :seasons="seasons"
            :selected-season="selectedSeason"
            :selected-leagues="selectedLeagues"
            :available-leagues="availableLeagues"
            :selected-additional-filter="selectedAdditionalFilter"
            @update:selectedSeason="val => selectedSeason = val"
            @update:selectedLeagues="val => selectedLeagues = val"
            @update:additionalFilter="val => selectedAdditionalFilter = val"
          />
          
          <!-- Main Content -->
          <div class="flex-1 flex flex-col bg-gray-50 h-full">
            <!-- Header -->
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 class="text-xl font-semibold text-gray-900">
                  Global Parameters
                  <span class="text-sm text-gray-500 ml-2">
                    Apply to {{ selectedTeam === 'both' ? 'both teams' : selectedTeam === 'home' ? 'home team' : 'away team' }}
                  </span>
                </h3>
                
                <!-- Team Selection -->
                <div class="mt-3 grid grid-cols-3 gap-3">
                  <label class="relative cursor-pointer">
                    <input
                      type="radio"
                      v-model="selectedTeam"
                      value="both"
                      class="sr-only"
                    />
                    <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                         :class="selectedTeam === 'both' 
                           ? 'border-pink-500 bg-pink-50 shadow-sm' 
                           : 'border-gray-200 bg-white hover:border-gray-300'">
                      <div class="flex items-center gap-2">
                        <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                             :class="selectedTeam === 'both' 
                               ? 'border-pink-500 bg-pink-500' 
                               : 'border-gray-300'">
                          <div v-if="selectedTeam === 'both'" class="w-2 h-2 rounded-full bg-white"></div>
                        </div>
                        <div>
                          <div class="font-medium text-sm" :class="selectedTeam === 'both' ? 'text-pink-700' : 'text-gray-700'">
                            Both Teams
                          </div>
                          <div class="text-xs" :class="selectedTeam === 'both' ? 'text-pink-600' : 'text-gray-500'">
                            Apply to all players
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                  
                  <label class="relative cursor-pointer">
                    <input
                      type="radio"
                      v-model="selectedTeam"
                      value="home"
                      class="sr-only"
                    />
                    <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                         :class="selectedTeam === 'home' 
                           ? 'border-blue-500 bg-blue-50 shadow-sm' 
                           : 'border-gray-200 bg-white hover:border-gray-300'">
                      <div class="flex items-center gap-2">
                        <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                             :class="selectedTeam === 'home' 
                               ? 'border-blue-500 bg-blue-500' 
                               : 'border-gray-300'">
                          <div v-if="selectedTeam === 'home'" class="w-2 h-2 rounded-full bg-white"></div>
                        </div>
                        <div>
                          <div class="font-medium text-sm" :class="selectedTeam === 'home' ? 'text-blue-700' : 'text-gray-700'">
                            Home Team
                          </div>
                          <div class="text-xs" :class="selectedTeam === 'home' ? 'text-blue-600' : 'text-gray-500'">
                            Home players only
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                  
                  <label class="relative cursor-pointer">
                    <input
                      type="radio"
                      v-model="selectedTeam"
                      value="away"
                      class="sr-only"
                    />
                    <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                         :class="selectedTeam === 'away' 
                           ? 'border-red-500 bg-red-50 shadow-sm' 
                           : 'border-gray-200 bg-white hover:border-gray-300'">
                      <div class="flex items-center gap-2">
                        <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                             :class="selectedTeam === 'away' 
                               ? 'border-red-500 bg-red-500' 
                               : 'border-gray-300'">
                          <div v-if="selectedTeam === 'away'" class="w-2 h-2 rounded-full bg-white"></div>
                        </div>
                        <div>
                          <div class="font-medium text-sm" :class="selectedTeam === 'away' ? 'text-red-700' : 'text-gray-700'">
                            Away Team
                          </div>
                          <div class="text-xs" :class="selectedTeam === 'away' ? 'text-red-600' : 'text-gray-500'">
                            Away players only
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
                
                <!-- Application Mode Selection -->
                <div class="mt-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Application Mode</label>
                  <div class="grid grid-cols-2 gap-3">
                    <!-- Merge Mode -->
                    <label class="relative cursor-pointer">
                      <input
                        type="radio"
                        v-model="applicationMode"
                        value="merge"
                        class="sr-only"
                      />
                      <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                           :class="applicationMode === 'merge' 
                             ? 'border-green-500 bg-green-50 shadow-sm' 
                             : 'border-gray-200 bg-white hover:border-gray-300'">
                        <div class="flex items-center gap-2">
                          <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                               :class="applicationMode === 'merge' 
                                 ? 'border-green-500 bg-green-500' 
                                 : 'border-gray-300'">
                            <div v-if="applicationMode === 'merge'" class="w-2 h-2 rounded-full bg-white"></div>
                          </div>
                          <div>
                            <div class="font-medium text-sm" :class="applicationMode === 'merge' ? 'text-green-700' : 'text-gray-700'">
                              Merge
                            </div>
                            <div class="text-xs" :class="applicationMode === 'merge' ? 'text-green-600' : 'text-gray-500'">
                              Add to existing filters
                            </div>
                          </div>
                        </div>
                      </div>
                    </label>
                    
                    <!-- Overwrite Mode -->
                    <label class="relative cursor-pointer">
                      <input
                        type="radio"
                        v-model="applicationMode"
                        value="overwrite"
                        class="sr-only"
                      />
                      <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                           :class="applicationMode === 'overwrite' 
                             ? 'border-orange-500 bg-orange-50 shadow-sm' 
                             : 'border-gray-200 bg-white hover:border-gray-300'">
                        <div class="flex items-center gap-2">
                          <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                               :class="applicationMode === 'overwrite' 
                                 ? 'border-orange-500 bg-orange-500' 
                                 : 'border-gray-300'">
                            <div v-if="applicationMode === 'overwrite'" class="w-2 h-2 rounded-full bg-white"></div>
                          </div>
                          <div>
                            <div class="font-medium text-sm" :class="applicationMode === 'overwrite' ? 'text-orange-700' : 'text-gray-700'">
                              Overwrite
                            </div>
                            <div class="text-xs" :class="applicationMode === 'overwrite' ? 'text-orange-600' : 'text-gray-500'">
                              Replace all filters
                            </div>
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              
              <!-- Header Buttons -->
              <div class="flex items-center gap-2">
                <!-- Apply Button -->
                <button
                  @click="applyGlobalParameters"
                  class="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
                >
                  <i class="fas fa-check"></i>
                  Apply to {{ selectedTeam === 'both' ? 'Both Teams' : selectedTeam === 'home' ? 'Home Team' : 'Away Team' }}
                </button>
                
                <!-- Split Mode Button -->
                <button
                  @click="toggleSplitMode"
                  class="bg-white hover:bg-gray-50 border-2 rounded-lg p-2 transition-all duration-200 shadow-sm"
                  :class="isSplitMode ? 'border-blue-500 text-blue-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'"
                  :title="isSplitMode ? 'Exit Split View' : 'Open in Split View'"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path v-if="!isSplitMode" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <!-- Close Button -->
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-500 focus:outline-none bg-white hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200"
                  @click="emit('close')"
                >
                  <span class="sr-only">Close</span>
                  <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Content Area -->
            <div class="flex-1 overflow-y-auto p-2">
              <div class="w-full">
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex-1 flex flex-col">
                  <h4 class="text-lg font-semibold text-gray-900 mb-4">Global Parameters Overview</h4>
                  
                  <div class="flex-1 grid grid-cols-1 gap-6">
                    <!-- Selected Seasons -->
                    <div>
                      <h5 class="font-medium text-gray-700 mb-2">Selected Seasons</h5>
                      <div v-if="selectedSeason.length > 0" class="flex flex-wrap gap-2">
                        <span v-for="season in selectedSeason" :key="season" 
                              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                          {{ season }}
                        </span>
                      </div>
                      <p v-else class="text-gray-500 text-sm">No seasons selected</p>
                    </div>
                    
                    <!-- Selected Leagues -->
                    <div>
                      <h5 class="font-medium text-gray-700 mb-2">Selected Leagues</h5>
                      <div v-if="selectedLeagues.length > 0" class="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        <span v-for="leagueId in selectedLeagues" :key="leagueId" 
                              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {{ availableLeagues.find(l => l.id === leagueId)?.name || leagueId }}
                        </span>
                      </div>
                      <p v-else class="text-gray-500 text-sm">No leagues selected</p>
                    </div>
                  </div>
                  
                  <!-- Additional Filter -->
                  <div class="mt-6">
                    <h5 class="font-medium text-gray-700 mb-2">Additional Filter</h5>
                    <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      {{ selectedAdditionalFilter === 'allGames' ? 'All Games' : 
                         selectedAdditionalFilter === 'lastFiveGames' ? 'Last 5 Games' : 
                         'Rest of Season (Without Last 5)' }}
                    </span>
                  </div>
                  
                  <!-- Games Preview Section -->
                  <div class="mt-6">
                    <h5 class="font-medium text-gray-700 mb-3">Games Preview</h5>
                    <div v-if="loadingGames" class="flex items-center justify-center py-4">
                      <i class="fas fa-spinner fa-spin text-gray-400 mr-2"></i>
                      <span class="text-gray-500">Loading games...</span>
                    </div>
                    <div v-else-if="filteredGames.length > 0" class="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
                      <div class="text-sm text-gray-600 mb-2">
                        Showing {{ filteredGames.length }} games that match your filters:
                      </div>
                      <div class="overflow-x-auto">
                        <table class="w-full text-sm">
                          <thead>
                            <tr class="bg-gray-50 border-b">
                              <th class="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                              <th class="text-left py-2 px-3 font-medium text-gray-700">Teams</th>
                              <th class="text-left py-2 px-3 font-medium text-gray-700">League</th>
                              <th class="text-left py-2 px-3 font-medium text-gray-700">Season</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="game in filteredGames" :key="game.id" class="border-b hover:bg-gray-50">
                              <td class="py-2 px-3 text-gray-600">{{ game.game_date }}</td>
                              <td class="py-2 px-3 font-medium">{{ game.team }} vs {{ game.opponent_team_name }}</td>
                              <td class="py-2 px-3">
                                <span class="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">{{ game.league_name }}</span>
                              </td>
                              <td class="py-2 px-3">
                                <span class="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">{{ game.season }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-else-if="selectedSeason.length > 0 || selectedLeagues.length > 0" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div class="flex items-center">
                        <i class="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
                        <span class="text-yellow-700 text-sm">No games found matching the selected filters.</span>
                      </div>
                    </div>
                    <div v-else class="bg-gray-50 rounded-lg p-4">
                      <div class="text-gray-500 text-sm">Select seasons and leagues to see matching games.</div>
                    </div>
                  </div>
                  
                  <!-- Info Text -->
                  <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div class="flex">
                      <div class="flex-shrink-0">
                        <i class="fas fa-info-circle text-blue-400"></i>
                      </div>
                      <div class="ml-3">
                        <p class="text-sm text-blue-700">
                          These global parameters will be applied to {{ selectedTeam === 'both' ? 'all players in both teams' : selectedTeam === 'home' ? 'all players in the home team' : 'all players in the away team' }} for the current fixture. 
                          Individual player parameters can still be customized in their respective detail modals.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Split Mode using Teleport -->
  <Teleport v-if="show && isSplitMode" to="#split-panel .overflow-auto">
    <div class="h-full bg-white">
      <!-- Duplicate Modal Content for Split Mode -->
      <div class="w-full h-full flex">
        <!-- Sidebar -->
        <GlobalParametersSidebar
          :seasons="seasons"
          :selected-season="selectedSeason"
          :selected-leagues="selectedLeagues"
          :available-leagues="availableLeagues"
          :selected-additional-filter="selectedAdditionalFilter"
          @update:selectedSeason="val => selectedSeason = val"
          @update:selectedLeagues="val => selectedLeagues = val"
          @update:additionalFilter="val => selectedAdditionalFilter = val"
        />
        
        <!-- Main Content -->
        <div class="flex-1 flex flex-col bg-gray-50 h-full">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 class="text-xl font-semibold text-gray-900">
                Global Parameters
                <span class="text-sm text-gray-500 ml-2">
                  Apply to {{ selectedTeam === 'both' ? 'both teams' : selectedTeam === 'home' ? 'home team' : 'away team' }}
                </span>
              </h3>
              
              <!-- Team Selection -->
              <div class="mt-3 grid grid-cols-3 gap-3">
                <label class="relative cursor-pointer">
                  <input
                    type="radio"
                    v-model="selectedTeam"
                    value="both"
                    class="sr-only"
                  />
                  <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                       :class="selectedTeam === 'both' 
                         ? 'border-pink-500 bg-pink-50 shadow-sm' 
                         : 'border-gray-200 bg-white hover:border-gray-300'">
                    <div class="flex items-center gap-2">
                      <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                           :class="selectedTeam === 'both' 
                             ? 'border-pink-500 bg-pink-500' 
                             : 'border-gray-300'">
                        <div v-if="selectedTeam === 'both'" class="w-2 h-2 rounded-full bg-white"></div>
                      </div>
                      <div>
                        <div class="font-medium text-sm" :class="selectedTeam === 'both' ? 'text-pink-700' : 'text-gray-700'">
                          Both Teams
                        </div>
                        <div class="text-xs" :class="selectedTeam === 'both' ? 'text-pink-600' : 'text-gray-500'">
                          Apply to all players
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
                
                <label class="relative cursor-pointer">
                  <input
                    type="radio"
                    v-model="selectedTeam"
                    value="home"
                    class="sr-only"
                  />
                  <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                       :class="selectedTeam === 'home' 
                         ? 'border-blue-500 bg-blue-50 shadow-sm' 
                         : 'border-gray-200 bg-white hover:border-gray-300'">
                    <div class="flex items-center gap-2">
                      <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                           :class="selectedTeam === 'home' 
                             ? 'border-blue-500 bg-blue-500' 
                             : 'border-gray-300'">
                        <div v-if="selectedTeam === 'home'" class="w-2 h-2 rounded-full bg-white"></div>
                      </div>
                      <div>
                        <div class="font-medium text-sm" :class="selectedTeam === 'home' ? 'text-blue-700' : 'text-gray-700'">
                          Home Team
                        </div>
                        <div class="text-xs" :class="selectedTeam === 'home' ? 'text-blue-600' : 'text-gray-500'">
                          Home players only
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
                
                <label class="relative cursor-pointer">
                  <input
                    type="radio"
                    v-model="selectedTeam"
                    value="away"
                    class="sr-only"
                  />
                  <div class="p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-md"
                       :class="selectedTeam === 'away' 
                         ? 'border-red-500 bg-red-50 shadow-sm' 
                         : 'border-gray-200 bg-white hover:border-gray-300'">
                    <div class="flex items-center gap-2">
                      <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center"
                           :class="selectedTeam === 'away' 
                             ? 'border-red-500 bg-red-500' 
                             : 'border-gray-300'">
                        <div v-if="selectedTeam === 'away'" class="w-2 h-2 rounded-full bg-white"></div>
                      </div>
                      <div>
                        <div class="font-medium text-sm" :class="selectedTeam === 'away' ? 'text-red-700' : 'text-gray-700'">
                          Away Team
                        </div>
                        <div class="text-xs" :class="selectedTeam === 'away' ? 'text-red-600' : 'text-gray-500'">
                          Away players only
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
            
            <!-- Header Buttons -->
            <div class="flex items-center gap-2">
              <!-- Apply Button -->
              <button
                @click="applyGlobalParameters"
                class="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
              >
                <i class="fas fa-check"></i>
                Apply to {{ selectedTeam === 'both' ? 'Both Teams' : selectedTeam === 'home' ? 'Home Team' : 'Away Team' }}
              </button>
              
              <!-- Split Mode Button -->
              <button
                @click="toggleSplitMode"
                class="bg-white hover:bg-gray-50 border-2 rounded-lg p-2 transition-all duration-200 shadow-sm border-blue-500 text-blue-600"
                title="Exit Split View"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <!-- Close Button -->
              <button
                type="button"
                class="text-gray-400 hover:text-gray-500 focus:outline-none bg-white hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200"
                @click="emit('close')"
              >
                <span class="sr-only">Close</span>
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <!-- Content Area -->
          <div class="flex-1 overflow-y-auto p-2">
            <div class="w-full">
              <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex-1 flex flex-col">
                <h4 class="text-lg font-semibold text-gray-900 mb-4">Global Parameters Overview</h4>
                
                <div class="flex-1 grid grid-cols-1 gap-6">
                  <!-- Selected Seasons -->
                  <div>
                    <h5 class="font-medium text-gray-700 mb-2">Selected Seasons</h5>
                    <div v-if="selectedSeason.length > 0" class="flex flex-wrap gap-2">
                      <span v-for="season in selectedSeason" :key="season" 
                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                        {{ season }}
                      </span>
                    </div>
                    <p v-else class="text-gray-500 text-sm">No seasons selected</p>
                  </div>
                  
                  <!-- Selected Leagues -->
                  <div>
                    <h5 class="font-medium text-gray-700 mb-2">Selected Leagues</h5>
                    <div v-if="selectedLeagues.length > 0" class="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      <span v-for="leagueId in selectedLeagues" :key="leagueId" 
                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {{ availableLeagues.find(l => l.id === leagueId)?.name || leagueId }}
                      </span>
                    </div>
                    <p v-else class="text-gray-500 text-sm">No leagues selected</p>
                  </div>
                </div>
                
                <!-- Additional Filter -->
                <div class="mt-6">
                  <h5 class="font-medium text-gray-700 mb-2">Additional Filter</h5>
                  <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    {{ selectedAdditionalFilter === 'allGames' ? 'All Games' : 
                       selectedAdditionalFilter === 'lastFiveGames' ? 'Last 5 Games' : 
                       'Rest of Season (Without Last 5)' }}
                  </span>
                </div>
                
                <!-- Games Preview Section -->
                <div class="mt-6">
                  <h5 class="font-medium text-gray-700 mb-3">Games Preview</h5>
                  <div v-if="loadingGames" class="flex items-center justify-center py-4">
                    <i class="fas fa-spinner fa-spin text-gray-400 mr-2"></i>
                    <span class="text-gray-500">Loading games...</span>
                  </div>
                   <div v-else-if="filteredGames.length > 0" class="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
                    <div class="text-sm text-gray-600 mb-2">
                      Showing {{ filteredGames.length }} games that match your filters:
                    </div>
                    <div class="overflow-x-auto">
                      <table class="w-full text-sm">
                        <thead>
                          <tr class="bg-gray-50 border-b">
                            <th class="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                            <th class="text-left py-2 px-3 font-medium text-gray-700">Teams</th>
                            <th class="text-left py-2 px-3 font-medium text-gray-700">League</th>
                            <th class="text-left py-2 px-3 font-medium text-gray-700">Season</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="game in filteredGames" :key="game.id" class="border-b hover:bg-gray-50">
                            <td class="py-2 px-3 text-gray-600">{{ game.game_date }}</td>
                            <td class="py-2 px-3 font-medium">{{ game.team }} vs {{ game.opponent_team_name }}</td>
                            <td class="py-2 px-3">
                              <span class="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">{{ game.league_name }}</span>
                            </td>
                            <td class="py-2 px-3">
                              <span class="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">{{ game.season }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div v-else-if="selectedSeason.length > 0 || selectedLeagues.length > 0" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div class="flex items-center">
                      <i class="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
                      <span class="text-yellow-700 text-sm">No games found matching the selected filters.</span>
                    </div>
                  </div>
                  <div v-else class="bg-gray-50 rounded-lg p-4">
                    <div class="text-gray-500 text-sm">Select seasons and leagues to see matching games.</div>
                  </div>
                </div>
                
                <!-- Info Text -->
                <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <i class="fas fa-info-circle text-blue-400"></i>
                    </div>
                    <div class="ml-3">
                      <p class="text-sm text-blue-700">
                        These global parameters will be applied to {{ selectedTeam === 'both' ? 'all players in both teams' : selectedTeam === 'home' ? 'all players in the home team' : 'all players in the away team' }} for the current fixture. 
                        Individual player parameters can still be customized in their respective detail modals.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Success/Error Notification Popup -->
  <div v-if="showNotification" class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeNotification"></div>
    <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div v-if="notificationData.type === 'success'" class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <i class="fas fa-check text-green-600"></i>
          </div>
          <div v-else class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <i class="fas fa-exclamation-triangle text-red-600"></i>
          </div>
          <h3 class="text-lg font-semibold" :class="notificationData.type === 'success' ? 'text-green-800' : 'text-red-800'">
            {{ notificationData.title }}
          </h3>
        </div>
        <button @click="closeNotification" class="text-gray-400 hover:text-gray-600">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <!-- Content -->
      <div class="px-6 py-4">
        <p class="text-gray-700 mb-4">{{ notificationData.message }}</p>
        
        <!-- Success Details -->
        <div v-if="notificationData.type === 'success'" class="space-y-3">
          <div class="bg-green-50 border border-green-200 rounded-lg p-3">
            <div class="flex items-center gap-2 mb-2">
              <i class="fas fa-users text-green-600"></i>
              <span class="font-medium text-green-800">Players Updated</span>
            </div>
            <p class="text-sm text-green-700">
              {{ notificationData.details.updatedCount }} players in {{ notificationData.details.teamType }} team
            </p>
          </div>
          
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex items-center gap-2 mb-2">
              <i class="fas fa-cogs text-blue-600"></i>
              <span class="font-medium text-blue-800">Application Mode</span>
            </div>
            <p class="text-sm text-blue-700">
              {{ notificationData.details.applicationMode === 'merge' ? 'Merged with existing filters' : 'Replaced all existing filters' }}
            </p>
          </div>
          
          <div v-if="notificationData.details.appliedFilters" class="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div class="flex items-center gap-2 mb-2">
              <i class="fas fa-filter text-gray-600"></i>
              <span class="font-medium text-gray-800">Applied Filters</span>
            </div>
            <div class="text-sm text-gray-700">
              <div v-if="notificationData.details.appliedFilters.seasons?.length > 0">
                <strong>Seasons:</strong> {{ notificationData.details.appliedFilters.seasons.join(', ') }}
              </div>
              <div v-if="notificationData.details.appliedFilters.leagues?.length > 0" class="mt-1">
                <strong>Leagues:</strong> {{ notificationData.details.appliedFilters.leagues.slice(0, 3).join(', ') }}
                <span v-if="notificationData.details.appliedFilters.leagues.length > 3">
                  and {{ notificationData.details.appliedFilters.leagues.length - 3 }} more
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Error Details -->
        <div v-else class="bg-red-50 border border-red-200 rounded-lg p-3">
          <div class="flex items-center gap-2 mb-2">
            <i class="fas fa-bug text-red-600"></i>
            <span class="font-medium text-red-800">Error Details</span>
          </div>
          <p class="text-sm text-red-700">{{ notificationData.details.error }}</p>
          <p v-if="notificationData.details.status" class="text-xs text-red-600 mt-1">
            Status: {{ notificationData.details.status }}
          </p>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-end">
        <button 
          @click="closeNotification"
          class="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>
